import { useTranslation } from 'react-i18next';
import { ChipInfo } from './ChipInfo';
import { chipHelp, type ScorecardChipKey } from './chipHelp';
import { DocsToReviewChip } from './DocsToReviewChip';
import { PaymentAttentionChips } from './PaymentAttentionChips';
import type { OpsScorecardMeResponse } from '../../api/types';
import {
  durationAccent,
  fmtDuration,
  fmtRate,
  rateAccent,
} from './scorecardFormat';
import styles from './ScorecardStrip.module.css';

export interface ScorecardStripProps {
  scorecard?: OpsScorecardMeResponse;
  isLoading?: boolean;
  activeChipKey?: string | null;
  onChipClick?: (key: string, shipmentIds: number[], isUnassigned: boolean) => void;
  /** Uploaded documents waiting for review; undefined while loading or on error (shows "—"). */
  docsToReviewCount?: number;
}

export function ScorecardStrip({
  scorecard,
  isLoading,
  activeChipKey,
  onChipClick,
  docsToReviewCount,
}: ScorecardStripProps) {
  const { t } = useTranslation();
  if (isLoading && !scorecard) {
    return (
      <div className={`${styles.strip} ${styles.stripLoading}`} aria-busy="true">
        <div className={styles.headlineLabel}>{t('scorecard.scorecardStrip.headlineLoading')}</div>
      </div>
    );
  }

  if (!scorecard) return null;

  const { target, targets, metrics, needsAttention } = scorecard;
  const fulfilled = metrics.fulfilled;
  const fulfilledDisplay = fulfilled != null ? fulfilled : '—';
  const progressPct =
    fulfilled != null && target > 0
      ? Math.min(100, Math.round((fulfilled / target) * 100))
      : 0;

  const claimAccent = durationAccent(metrics.medianClaimMinutes, targets.claimTargetMinutes);
  const podAccent = durationAccent(metrics.medianPodCloseMinutes, targets.podCloseTargetHours * 60);
  const fulfilRateAccent = rateAccent(metrics.fulfilmentRate);
  const onTimeAccent = rateAccent(metrics.onTimePickupRate);

  const accentClass = (accent: 'default' | 'green' | 'amber' | 'red') => {
    switch (accent) {
      case 'green':
        return styles.accentGreen;
      case 'amber':
        return styles.accentAmber;
      case 'red':
        return styles.accentRed;
      default:
        return styles.accentDefault;
    }
  };

  const chips = [
    {
      key: 'unclaimed' as ScorecardChipKey,
      label: 'Unclaimed',
      item: needsAttention.unclaimedOrders,
      isUnassigned: true,
      alert: needsAttention.unclaimedOrders.count > 0 ? styles.chipAlert : '',
    },
    {
      key: 'driver' as ScorecardChipKey,
      label: 'Awaiting driver',
      item: needsAttention.awaitingDriver,
      isUnassigned: false,
      alert: needsAttention.awaitingDriver.count > 0 ? styles.chipWarning : '',
    },
    {
      key: 'pickup' as ScorecardChipKey,
      label: 'Pickup at risk',
      item: needsAttention.pickupAtRisk,
      isUnassigned: false,
      alert: needsAttention.pickupAtRisk.count > 0 ? styles.chipAlert : '',
    },
    {
      key: 'pod' as ScorecardChipKey,
      label: 'POD not closed',
      item: needsAttention.podNotClosed,
      isUnassigned: false,
      alert: needsAttention.podNotClosed.count > 0 ? styles.chipWarning : '',
    },
    {
      key: 'stopped' as ScorecardChipKey,
      label: 'Stopped',
      item: needsAttention.stopped,
      isUnassigned: false,
      alert: needsAttention.stopped.count > 0 ? styles.chipWarning : '',
    },
    {
      key: 'biddingClosed' as ScorecardChipKey,
      label: 'Bidding closed',
      item: needsAttention.biddingClosed,
      isUnassigned: false,
      alert: needsAttention.biddingClosed.count > 0 ? styles.chipAlert : '',
    },
  ];

  return (
    <div className={styles.strip} data-testid="ops-scorecard-strip">
      <div className={styles.topRow}>
        <div className={styles.headlineGroup}>
          <div className={styles.headlineLabel}>{t('scorecard.scorecardStrip.headline')}</div>
          <div className={styles.headlineValue}>
            <span>{t('scorecard.scorecardStrip.fulfilled', { value: fulfilledDisplay })}</span>
            <span className={styles.headlineTarget}>{t('scorecard.scorecardStrip.target', { target })}</span>
          </div>
          <div
            className={styles.progressWrap}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('scorecard.scorecardStrip.progressLabel')}
          >
            <div
              className={`${styles.progressBar} ${
                fulfilled != null && fulfilled >= target ? styles.progressBarCompleted : ''
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className={styles.secondaryMetrics}>
          <div className={styles.metricPill} data-testid="metric-fulfilment-rate">
            <span className={styles.metricLabel}>{t('scorecard.scorecardStrip.fulfilmentRate')}</span>
            <span className={`${styles.metricValue} ${accentClass(fulfilRateAccent)}`}>
              {fmtRate(metrics.fulfilmentRate)}
            </span>
          </div>

          <div className={styles.metricPill} data-testid="metric-time-to-claim">
            <span className={styles.metricLabel}>{t('scorecard.scorecardStrip.timeToClaim')}</span>
            <span className={`${styles.metricValue} ${accentClass(claimAccent)}`}>
              {fmtDuration(metrics.medianClaimMinutes)}
            </span>
          </div>

          <div className={styles.metricPill} data-testid="metric-ontime-pickup">
            <span className={styles.metricLabel}>{t('scorecard.scorecardStrip.onTimePickup')}</span>
            <span className={`${styles.metricValue} ${accentClass(onTimeAccent)}`}>
              {fmtRate(metrics.onTimePickupRate)}
            </span>
          </div>

          <div className={styles.metricPill} data-testid="metric-pod-closure">
            <span className={styles.metricLabel}>{t('scorecard.scorecardStrip.podClosure')}</span>
            <span className={`${styles.metricValue} ${accentClass(podAccent)}`}>
              {fmtDuration(metrics.medianPodCloseMinutes)}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.needsAttentionRow}>
        <span className={styles.attentionLabel}>{t('scorecard.scorecardStrip.needsAttention')}</span>
        <PaymentAttentionChips attention={needsAttention} />
        <DocsToReviewChip count={docsToReviewCount} />
        {chips.map((c) => {
          const isActive = activeChipKey === c.key;
          return (
            <span key={c.key} className={styles.chipWithInfo}>
              <button
                type="button"
                className={`${styles.chip} ${isActive ? styles.chipActive : ''} ${c.alert}`}
                onClick={() => onChipClick?.(c.key, c.item.shipmentIds, c.isUnassigned)}
                aria-pressed={isActive}
              >
                <span>{c.label}</span>
                <span className={styles.chipBadge}>{c.item.count}</span>
              </button>
              <ChipInfo label={c.label} text={chipHelp(c.key, targets)} />
            </span>
          );
        })}
      </div>
    </div>
  );
}
